import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import throttle from 'lodash/throttle';

const openEvent = new Event('navigation-opened');
const closeEvent = new Event('navigation-closed');

const scrollableElements = document.querySelectorAll(
	'.block-navigation__menu, .block-navigation.is-style-full-width-dropdown .block-navigation-submenu, .block-navigation-submenu'
);

const navigationBlocks = document.querySelectorAll('.block-navigation');

const navigationHamburgers = document.querySelectorAll(
	'.block-navigation__hamburger'
);

navigationBlocks.forEach((navigationBlock, index) => {
	const menu = navigationBlock.querySelector('.block-navigation__menu');
	const hamburger = navigationBlock.querySelector(
		'.block-navigation__hamburger'
	);

	if (menu) {
		menu.setAttribute('role', 'dialog');
		menu.setAttribute('aria-modal', 'true');
		menu.setAttribute('aria-label', 'Main menu');
		menu.setAttribute('id', `main-navigation-dialog-${index}`);
	}

	if (hamburger && menu) {
		hamburger.setAttribute(
			'aria-controls',
			`main-navigation-dialog-${index}`
		);
		// aria-expanded is already being managed in your `toggleNavigationHamburger`
	}
});

window.addEventListener('DOMContentLoaded', () => {
	const logo = document.querySelector('.block-navigation__logo-link');
	const hamburger = document.querySelector('.block-navigation__hamburger');

	if (logo) logo.setAttribute('tabindex', '1');
	if (hamburger) hamburger.setAttribute('tabindex', '0');
});

const toggleNavigationHamburger = (e) => {
	const currentNavigationHamburger = e?.currentTarget ?? e;
	currentNavigationHamburger.classList.toggle('active');
	const currentNavigationBlock =
		currentNavigationHamburger.closest('.block-navigation');
	const menu = currentNavigationBlock.querySelector(
		'.block-navigation__menu'
	);

	const getFocusableElements = (container) => {
		return container.querySelectorAll(
			'a[href], button:not([disabled]), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
		);
	};

	const trapFocus = (e) => {
		const focusableElements = getFocusableElements(menu);
		const firstEl = focusableElements[0];
		const lastEl = focusableElements[focusableElements.length - 1];

		if (e.key !== 'Tab') return;

		if (e.shiftKey) {
			if (document.activeElement === firstEl) {
				e.preventDefault();
				lastEl.focus();
			}
		} else if (document.activeElement === lastEl) {
			e.preventDefault();
			firstEl.focus();
		}
	};

	if (currentNavigationHamburger.classList.contains('active')) {
		window.dispatchEvent(openEvent);
		setFirstLevelButtonBlockOffsets();
		setNavMenuActiveClass(currentNavigationHamburger, true);
		currentNavigationHamburger.setAttribute('aria-expanded', true);

		const focusableElements = getFocusableElements(menu);
		const firstEl = focusableElements[0];

		if (firstEl) firstEl.focus();

		menu.addEventListener('keydown', trapFocus);

		if (window.innerWidth < 992) {
			lockScroll();
		}
	} else {
		window.dispatchEvent(closeEvent);
		menu.removeEventListener('keydown', trapFocus);

		clearAllBodyScrollLocks();

		setNavMenuActiveClass(currentNavigationHamburger, false);
		currentNavigationHamburger.setAttribute('aria-expanded', false);
	}
};

const submenuClosed = () => {
	if (window.innerWidth >= 992) {
		clearAllBodyScrollLocks();
	}
};

const setFirstLevelButtonBlockOffsets = () => {
	if (0 === navigationBlocks.length) {
		return;
	}

	navigationBlocks.forEach((navigationBlock) => {
		const firstLevelButtonBlocks = navigationBlock.querySelectorAll(
			'.block-navigation__menu > .wp-block-button'
		);

		if (0 === firstLevelButtonBlocks.length) {
			return;
		}

		let totalOffset = 0;

		for (let i = firstLevelButtonBlocks.length - 1; i >= 0; i--) {
			const firstLevelButtonBlock = firstLevelButtonBlocks[i];

			firstLevelButtonBlock.style.setProperty(
				'--buttonOffset',
				`${totalOffset / 16}rem`
			);

			totalOffset += firstLevelButtonBlock.offsetHeight;
		}

		navigationBlock.style.setProperty(
			'--totalButtonOffset',
			`${totalOffset / 16}rem`
		);
	});
};

const setNavMenuActiveClass = (element, navActive = null) => {
	const currentElement = element?.detail?.currentElement ?? element;

	if (!currentElement) {
		return;
	}

	const currentNavigationBlock = currentElement.closest('.block-navigation');

	if (!currentNavigationBlock) {
		return;
	}

	if (null === navActive) {
		if (
			currentElement.classList.contains('active') ||
			currentElement.parentElement.classList.contains('active') ||
			currentNavigationBlock.querySelector(
				'.block-navigation__hamburger.active'
			)
		) {
			navActive = true;
		} else {
			navActive = false;
		}
	}

	if (true === navActive) {
		currentNavigationBlock.classList.add('active');
		document.addEventListener('keydown', clickOffMenu);
		document.addEventListener('click', clickOffMenu);
	} else {
		currentNavigationBlock.classList.remove('active');
		document.removeEventListener('keydown', clickOffMenu);
		document.removeEventListener('click', clickOffMenu);
	}

	navigationBlocks.forEach((navigationBlock) => {
		if (navigationBlock !== currentNavigationBlock) {
			navigationBlock.classList.remove('active');
		}
	});
};

const lockScroll = () => {
	if (scrollableElements.length) {
		scrollableElements.forEach(function (scrollableElement) {
			const storedRequestAnimationFrame = window.requestAnimationFrame;
			window.requestAnimationFrame = () => 42;

			disableBodyScroll(scrollableElement, {
				reserveScrollBarGap: true,
			});
			window.requestAnimationFrame = storedRequestAnimationFrame;
		});
	}
};

const clickOffMenu = (e) => {
	if (0 === navigationHamburgers.length) {
		return;
	}

	let closestMenu;
	let closestHamburger;

	if (e.key === undefined && e.target) {
		closestMenu = e.target.closest('.block-navigation__menu');
		closestHamburger = e.target.closest('.block-navigation__hamburger');
	}

	if (e.key && e.key !== 'Escape') {
		return;
	}

	if (
		closestMenu?.contains(e.target) ||
		closestHamburger?.contains(e.target)
	) {
		return;
	}

	navigationHamburgers.forEach((navigationHamburger) => {
		if (navigationHamburger.classList.contains('active')) {
			toggleNavigationHamburger(navigationHamburger);
		}
	});

	window.dispatchEvent(closeEvent);
};

if (navigationHamburgers.length > 0) {
	navigationHamburgers.forEach((navigationHamburger) => {
		navigationHamburger.addEventListener(
			'click',
			toggleNavigationHamburger
		);
	});
}

setFirstLevelButtonBlockOffsets();

if (navigationBlocks.length > 0) {
	navigationBlocks.forEach((navigationBlock) => {
		navigationBlock.classList.add('block-navigation--initialized');
	});
}

window.addEventListener('navigation-submenu-closed', submenuClosed);
window.addEventListener('navigation-submenu-closed', setNavMenuActiveClass);
window.addEventListener('navigation-submenu-opened', setNavMenuActiveClass);
window.addEventListener(
	'resize',
	throttle(setFirstLevelButtonBlockOffsets, 100)
);

document.addEventListener('DOMContentLoaded', () => {
	// Select the last `.wp-block-button.is-style-primary` within `.block-navigation__menu`
	const primaryButton = document.querySelector(
		'.block-navigation__menu .wp-block-button.is-style-primary:last-of-type'
	);

	if (primaryButton) {
		// Clone the button
		const clonedButton = primaryButton.cloneNode(true);

		// Create a parent wrapper with the class `show-on-mobile`
		const wrapper = document.createElement('div');
		wrapper.classList.add('show-on-mobile');

		// Append the cloned button to the wrapper
		wrapper.appendChild(clonedButton);

		// Append the wrapper to the `#page` element
		const pageElement = document.getElementById('page');
		if (pageElement) {
			pageElement.appendChild(wrapper);
		}
	}
});
